import { useEffect, useState } from "react";
import { Alert, Box, Card, CardContent, CircularProgress } from "@mui/material";
import axios from "axios";
import AuditHeader from "./audit-header";
import AuditTypeSelector from "./audit-type-selector";
import AuditFiltersPanel, { AuditFiltersState } from "./audit-filters-panel";
import AuditDatabaseAccordion from "./audit-database-accordion";
import { useAuditTypes } from "./use-audit-types";
import { getDefaultFilters } from "./audit-utils";

const buildDefaultFiltersState = (
  columns: { columnName: string; isFilterable: boolean }[],
): AuditFiltersState => ({
  filters: getDefaultFilters(columns),
  dateFrom: null,
  dateTo: null,
  includeSensitive: false,
});

const Audit = () => {
  const { data, error, isLoading } = useAuditTypes();
  const types = data?.results ?? [];

  const [typeKey, setTypeKey] = useState("");
  const [filtersState, setFiltersState] = useState<AuditFiltersState>({
    filters: {},
    dateFrom: null,
    dateTo: null,
    includeSensitive: false,
  });
  const [sortColumn, setSortColumn] = useState("");
  const [sortDescending, setSortDescending] = useState(false);
  const [expandedDatabase, setExpandedDatabase] = useState<string | false>(
    false,
  );

  const selectedType = types.find((type) => type.typeKey === typeKey);

  useEffect(() => {
    if (!typeKey && types.length > 0) {
      setTypeKey(types[0].typeKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types]);

  useEffect(() => {
    if (!selectedType) return;

    setFiltersState(buildDefaultFiltersState(selectedType.columns));
    setSortColumn(selectedType.defaultSortColumn);
    setSortDescending(selectedType.defaultSortDescending);
    setExpandedDatabase(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeKey]);

  const handleClearFilters = () => {
    if (!selectedType) return;
    setFiltersState(buildDefaultFiltersState(selectedType.columns));
  };

  const handleSortChange = (column: string, descending: boolean) => {
    setSortColumn(column);
    setSortDescending(descending);
  };

  const isPermissionError =
    axios.isAxiosError(error) &&
    (error.response?.status === 401 || error.response?.status === 403);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {isPermissionError
          ? "You do not have permission to view audit data."
          : "Failed to load audit types. Please try again later."}
      </Alert>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        minHeight: "calc(100vh - 140px)",
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: "grey.200",
        background: "linear-gradient(to bottom, #ffffff, #fafafa)",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <AuditHeader />

        <Box sx={{ mb: 3 }}>
          <AuditTypeSelector value={typeKey} onChange={setTypeKey} />
        </Box>

        {types.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No audit types configured.
          </Alert>
        )}

        {selectedType && (
          <>
            <Box sx={{ mb: 3 }}>
              <AuditFiltersPanel
                type={selectedType}
                values={filtersState}
                onApply={setFiltersState}
                onClear={handleClearFilters}
              />
            </Box>

            {selectedType.applicableDatabases.map((databaseName) => (
              <AuditDatabaseAccordion
                key={databaseName}
                type={selectedType}
                databaseName={databaseName}
                expanded={expandedDatabase === databaseName}
                onToggle={(isExpanded) =>
                  setExpandedDatabase(isExpanded ? databaseName : false)
                }
                filters={filtersState}
                sortColumn={sortColumn}
                sortDescending={sortDescending}
                onSortChange={handleSortChange}
              />
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default Audit;
